import { useState, useEffect } from 'react';
import { X, Loader, Trash2, Plus, Music, ExternalLink } from 'lucide-react';
import { supabase, AudioPin } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type Zone = {
  id: string;
  name: string;
  description: string;
  playlist_id: string | null;
  latitude?: number;
  longitude?: number;
};

type EditZoneModalProps = {
  zone: Zone;
  onClose: () => void;
  onSuccess: () => void;
};

export function EditZoneModal({ zone, onClose, onSuccess }: EditZoneModalProps) {
  const { user } = useAuth();
  const [name, setName] = useState(zone.name);
  const [description, setDescription] = useState(zone.description);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Playlist management
  const [audioPins, setAudioPins] = useState<AudioPin[]>([]);
  const [loadingPins, setLoadingPins] = useState(true);
  const [showAddSound, setShowAddSound] = useState(false);
  const [newSoundTitle, setNewSoundTitle] = useState('');
  const [newSoundUrl, setNewSoundUrl] = useState('');
  const [newSoundDescription, setNewSoundDescription] = useState('');
  const [addingSoundLoading, setAddingSoundLoading] = useState(false);

  // Load audio pins for this zone
  useEffect(() => {
    loadAudioPins();
  }, [zone.id]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else if (showAddSound) {
          setShowAddSound(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, showDeleteConfirm, showAddSound]);

  const loadAudioPins = async () => {
    setLoadingPins(true);
    try {
      const { data, error } = await supabase
        .from('audio_pins')
        .select('*')
        .eq('zone_id', zone.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAudioPins(data || []);
    } catch (err) {
      console.error('Failed to load audio pins:', err);
    } finally {
      setLoadingPins(false);
    }
  };

  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  const handleAddSound = async () => {
    if (!newSoundTitle.trim() || !newSoundUrl.trim()) {
      setError('Title and YouTube URL are required');
      return;
    }

    setAddingSoundLoading(true);
    setError(null);

    try {
      if (!user) {
        throw new Error('You must be logged in to add sound');
      }

      const videoId = extractVideoId(newSoundUrl);
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }

      const lat = zone.latitude || 0;
      const lng = zone.longitude || 0;

      const { data, error: insertError } = await supabase.rpc('insert_audio_pin_with_location', {
        p_title: newSoundTitle.trim(),
        p_description: newSoundDescription.trim() || null,
        p_youtube_url: newSoundUrl.trim(),
        p_youtube_video_id: videoId,
        p_latitude: lat,
        p_longitude: lng,
        p_zone_id: zone.id,
        p_user_id: user.id,
        p_is_public: true,
      });

      if (insertError) throw insertError;

      await loadAudioPins();
      setNewSoundTitle('');
      setNewSoundUrl('');
      setNewSoundDescription('');
      setShowAddSound(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add sound');
    } finally {
      setAddingSoundLoading(false);
    }
  };

  const handleRemoveSound = async (pinId: string) => {
    if (!confirm('Remove this sound from the zone?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('audio_pins')
        .delete()
        .eq('id', pinId);

      if (deleteError) throw deleteError;

      setAudioPins(audioPins.filter(pin => pin.id !== pinId));
    } catch (err) {
      setError('Failed to remove sound');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('sound_zones')
        .update({
          name,
          description,
          updated_at: new Date().toISOString()
        })
        .eq('id', zone.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update zone');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('sound_zones')
        .delete()
        .eq('id', zone.id);

      if (deleteError) throw deleteError;

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete zone');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Edit Sound Zone</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zone Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter zone name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
                placeholder="Enter zone description"
              />
            </div>
          </div>

          {/* Playlist Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Playlist</h3>
                <span className="text-sm text-gray-500">({audioPins.length})</span>
              </div>
              {!showAddSound && (
                <button
                  type="button"
                  onClick={() => setShowAddSound(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Add Sound
                </button>
              )}
            </div>

            {/* Add Sound Form */}
            {showAddSound && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newSoundTitle}
                    onChange={(e) => setNewSoundTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Enter sound title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    YouTube URL *
                  </label>
                  <input
                    type="url"
                    value={newSoundUrl}
                    onChange={(e) => setNewSoundUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newSoundDescription}
                    onChange={(e) => setNewSoundDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                    rows={2}
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSound(false);
                      setNewSoundTitle('');
                      setNewSoundUrl('');
                      setNewSoundDescription('');
                      setError(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddSound}
                    disabled={addingSoundLoading}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm font-semibold"
                  >
                    {addingSoundLoading ? 'Adding...' : 'Add Sound'}
                  </button>
                </div>
              </div>
            )}

            {/* Audio Pins List */}
            {loadingPins ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : audioPins.length === 0 ? (
              <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Music className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 text-sm">No sounds in this zone yet</p>
                <p className="text-gray-400 text-xs mt-1">Click "Add Sound" to add your first track</p>
              </div>
            ) : (
              <div className="space-y-2">
                {audioPins.map((pin) => (
                  <div
                    key={pin.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-16 h-16 flex-shrink-0 bg-gray-200 rounded overflow-hidden">
                      <img
                        src={`https://img.youtube.com/vi/${pin.youtube_video_id}/mqdefault.jpg`}
                        alt={pin.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm truncate">
                        {pin.title}
                      </h4>
                      {pin.description && (
                        <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                          {pin.description}
                        </p>
                      )}
                      <a
                        href={pin.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 mt-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View on YouTube
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSound(pin.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                      title="Remove sound"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Update Zone'
                )}
              </button>
            </div>

            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="w-full px-6 py-3 bg-red-50 text-red-600 border border-red-200 rounded-lg font-semibold hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Zone
              </button>
            ) : (
              <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                <p className="text-sm text-red-800 mb-3 font-medium">
                  Are you sure you want to delete this zone? This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-white border border-gray-600 text-gray-300 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {loading ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
